<template>
	<div v-if="coverageYears.length" class="coverage-grid" :class="{ 'is-selectable': selectable }">
		<div v-for="row in coverageYears" :key="row.year" class="coverage-row">
			<span class="coverage-year">{{ row.year }}</span>
			<span
				v-for="m in 12"
				:key="m"
				class="coverage-cell"
				:class="{
					'is-loaded': row.months[m],
					'is-selected': isSelected(row.year, m),
				}"
				:role="selectable && row.months[m] ? 'button' : null"
				:tabindex="selectable && row.months[m] ? 0 : null"
				:aria-pressed="selectable && row.months[m] ? String(isSelected(row.year, m)) : null"
				v-tooltip.top="coverageTitle(row.year, m, row.months[m])"
				@click="toggle(row.year, m, row.months[m])"
				@keydown.enter.prevent="toggle(row.year, m, row.months[m])"
				@keydown.space.prevent="toggle(row.year, m, row.months[m])"
			>
				{{ monthInitial(m) }}
			</span>
		</div>
	</div>
</template>

<script>
// Grid de cobertura mensual (año × 12): la celda teal es mes con datos.
// Compartido por las secciones de subida de la ficha (ventas/costos y mayor):
// recibe la cobertura ya derivada por el backend y no toca Vuex ni la API.
// El label/eyebrow lo pone el padre (cada sección tiene su propio estilo).
//
// Modo `selectable`: las celdas CON datos se pueden marcar (clic/Enter/
// Espacio) y el grid emite `update:selected` con la lista 'YYYY-MM' nueva —
// el padre la enlaza con `:selected.sync`. Las celdas sin datos no se
// seleccionan: no hay nada que borrar ahí.
const MONTH_INITIALS = ["E", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];
const MONTH_NAMES = [
	"Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
	"Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

export default {
	name: "MonthCoverageGrid",
	props: {
		// [{month: "YYYY-MM", documents: n}] ordenado cronológicamente.
		monthsLoaded: {
			type: Array,
			default: () => [],
		},
		selectable: {
			type: Boolean,
			default: false,
		},
		// Meses marcados ('YYYY-MM'); solo tiene efecto con `selectable`.
		selected: {
			type: Array,
			default: () => [],
		},
	},
	computed: {
		// Agrupada por año para el grid: [{year, months: {1..12: docs}}],
		// del año más viejo al más nuevo.
		coverageYears() {
			const byYear = {};
			this.monthsLoaded.forEach((item) => {
				const [year, month] = item.month.split("-");
				if (!byYear[year]) byYear[year] = {};
				byYear[year][parseInt(month, 10)] = item.documents;
			});
			return Object.keys(byYear)
				.sort()
				.map((year) => ({ year, months: byYear[year] }));
		},
	},
	methods: {
		monthInitial(month) {
			return MONTH_INITIALS[month - 1];
		},
		monthKey(year, month) {
			return `${year}-${String(month).padStart(2, "0")}`;
		},
		isSelected(year, month) {
			return this.selectable && this.selected.includes(this.monthKey(year, month));
		},
		toggle(year, month, documents) {
			if (!this.selectable || !documents) return;
			const key = this.monthKey(year, month);
			const next = this.selected.includes(key)
				? this.selected.filter((item) => item !== key)
				: [...this.selected, key].sort();
			this.$emit("update:selected", next);
		},
		coverageTitle(year, month, documents) {
			const name = `${MONTH_NAMES[month - 1]} ${year}`;
			if (!documents) return `${name}: sin documentos`;
			const count = documents === 1
				? `${name}: 1 documento`
				: `${name}: ${documents} documentos`;
			if (!this.selectable) return count;
			return this.isSelected(year, month)
				? `${count} · seleccionado`
				: `${count} · toca para seleccionar`;
		},
	},
};
</script>

<style scoped lang="scss">
// Tema del analista (var(--ap-*), elements/_analyst_theme.scss).
.coverage-grid {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.coverage-row {
	display: flex;
	align-items: center;
	gap: 4px;
}

.coverage-year {
	flex: none;
	width: 40px;
	font-size: 11px;
	font-weight: 600;
	color: var(--ap-slate);
	font-variant-numeric: tabular-nums;
}

.coverage-cell {
	flex: 1;
	max-width: 36px;
	height: 24px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 10px;
	font-weight: 600;
	color: var(--ap-muted);
	background-color: var(--ap-mist);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
	cursor: default;
}

.coverage-cell.is-loaded {
	color: var(--ap-teal-deep);
	background-color: var(--ap-teal-wash);
	border-color: var(--ap-teal);
}

// Modo seleccionable: solo las celdas con datos invitan al clic; la marcada
// pasa al registro de alerta (es lo que se va a eliminar).
.is-selectable .coverage-cell.is-loaded {
	cursor: pointer;
	transition: background-color 0.14s var(--ap-ease), color 0.14s var(--ap-ease),
		border-color 0.14s var(--ap-ease);

	&:hover {
		border-color: var(--ap-teal-deep);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: 1px;
	}
}

.is-selectable .coverage-cell.is-selected {
	color: var(--ap-paper);
	background-color: var(--ap-alert);
	border-color: var(--ap-alert);

	&:hover {
		border-color: var(--ap-alert);
	}
}
</style>
